import { put, takeLatest, call } from "redux-saga/effects";
import { ENUM_DATA } from "../../constData/enum";
import { setDrmExcelTableDataFailure, setDrmExcelTableDataSuccess } from "../reducers/excelDataSlice";
import { fetchDrmExcelData } from "../../service/fetchDrmExcelData";
import { convertToDDMMYYYY, modecreation } from "../../helpers";
import { mapDrmBaseFields } from "./drmDataMapper";

const getFieldValue = (value) => value ?? ENUM_DATA.Empty;

const getBucketValueFromQtyOrPct = (item) => {
  if (item?.qtyBucketType) {
    return item.qtyBucketType;
  }else if (item?.pctBucketType) {
    return item.pctBucketType;
  }
  return ENUM_DATA.Empty;
}

const ALLOWED_CHARGE_TYPES = new Set(["BAF", "CIVA", "ETS", "NA_FLAT", "CONT", "MILE"]);

const mapRateToChargeType = (item) => {
  const rateValue = getFieldValue(item?.rate);
  const emptyFields = {
    baf: ENUM_DATA.Empty,
    civa: ENUM_DATA.Empty,
    ets: ENUM_DATA.Empty,
  };

  switch (item?.lineHaulChargeType) {
    case "BAF":
      return { ...emptyFields, baf: rateValue, rate: ENUM_DATA.Empty };
    case "CIVA":
      return { ...emptyFields, civa: rateValue, rate: ENUM_DATA.Empty };
    case "ETS":
      return { ...emptyFields, ets: rateValue, rate: ENUM_DATA.Empty };
    case "NA_FLAT":
    case "CONT":
    case "MILE":
      return { ...emptyFields, lineHaulRate: rateValue };
    default:
      return { ...emptyFields, lineHaulRate: ENUM_DATA.Empty };
  }
};

const mapCommonFields = (item) => ({
  ...mapDrmBaseFields(item, { blankValue: ENUM_DATA.Empty }),
  minCarrierAllocationPercentage: ENUM_DATA.Empty,
  maxCarrierAllocationPercentage: item?.maxCarrierAllocationPercentage == null ? ENUM_DATA.Empty : item.maxCarrierAllocationPercentage,
  modeOfTransport: getFieldValue(item?.modeOfTransport),
  status: getFieldValue(item?.status),
  message: getFieldValue(item?.message),
  mode: modecreation(item?.modeOfTransport, item?.tariffId),
  region: getFieldValue(item?.region),
  carrierManager: getFieldValue(item?.carrierManager),
  primaryCustomers: getFieldValue(item?.primaryCustomers),
  versionNumber: getFieldValue(item?.versionNumber),
  errors: item?.errors?.length > 0 ? item.errors.map((error) => ({
    record: getFieldValue(error.columnName),
    errorMessage: getFieldValue(error.errorMessage)
  })) : [],
  defaultError: getFieldValue(item?.defaultError),
  systemRateId: getFieldValue(item?.systemRateId),
});

const mapBidSpecificFields = (item) => ({
  itemType: getFieldValue(item?.itemType),
  item: getFieldValue(item?.item),
  estimatedMileagePcMiler: getFieldValue(item?.estimatedMileagePcMiler),
  annualShipmentVolume: getFieldValue(item?.annualShipmentVolume),
  availableAnnualShipmentVolume: getFieldValue(item?.availableAnnualShipmentVolume),
  annualMaxShipmentVolume: getFieldValue(item?.annualMaxShipmentVolume),
  bidRateWoFuel: getFieldValue(item?.bidRateWoFuel),
  laneServiceType: getFieldValue(item?.laneServiceType),
  incoterm: getFieldValue(item?.incoterm),
  supplier: getFieldValue(item?.supplier),
  transitTime: getFieldValue(item?.transitTime),
  originPortCountry: getFieldValue(item?.originPortCountry),
  originPort: getFieldValue(item?.originPort),
  destinationTransportationMode: getFieldValue(item?.destinationTransportationMode),
  tradeLane: getFieldValue(item?.tradeLane),
  destinationPort: getFieldValue(item?.destinationPort),
  destinationPortCountry: getFieldValue(item?.destinationPortCountry),
  bucketType: getFieldValue(item?.bucketType),
  baf: getFieldValue(item?.baf),
  civa: getFieldValue(item?.civa),
  ets: getFieldValue(item?.ets),
  lineHaulRate: getFieldValue(item?.lineHaulRate),
  qtyStartDate: item?.effectiveDate ? convertToDDMMYYYY(item.effectiveDate) : ENUM_DATA.Empty,
  qtyEndDate: item?.expirationDate ? convertToDDMMYYYY(item.expirationDate) : ENUM_DATA.Empty,
  pctStartDate: item?.effectiveDate ? convertToDDMMYYYY(item.effectiveDate) : ENUM_DATA.Empty,
  pctEndDate: item?.expirationDate ? convertToDDMMYYYY(item.expirationDate) : ENUM_DATA.Empty,
  changeComment: getFieldValue(item?.changeComment),
  coupaFile: item?.coupaFile ? {
    id: getFieldValue(item.coupaFile.id),
    coupaFileName: getFieldValue(item.coupaFile.coupaFileName),
    status: getFieldValue(item?.status),
    coupaFileType: getFieldValue(item.coupaFile.coupaFileType),
    domesticCoupaFileData: null
  } : ENUM_DATA.Empty,
  bidFileName: getFieldValue(item?.coupaFile?.coupaFileName),
  coupaFileRecordErrors: item?.coupaFileRecordErrors?.length > 0 ? item.coupaFileRecordErrors.map((error) => ({
    record: getFieldValue(error.columnName),
    errorMessage: getFieldValue(error.errorMessage)
  })) : [],
});

const mapDailySpecificFields = (item) => ({
  rangeCode: getFieldValue(item?.rangeCode),
  rangeToUnits: getFieldValue(item?.rangeToUnits),
  rate: getFieldValue(item?.rate),
  bucketType: getBucketValueFromQtyOrPct(item),
  qtyBucketType: getFieldValue(item?.qtyBucketType),
  pctBucketType: getFieldValue(item?.pctBucketType),
  qtyStartDate: getFieldValue(item?.qtyStartDate),
  qtyEndDate: getFieldValue(item?.qtyEndDate),
  pctStartDate: getFieldValue(item?.pctStartDate),
  pctEndDate: getFieldValue(item?.pctEndDate),
  miles: getFieldValue(item?.miles),
  orderType: getFieldValue(item?.orderType),
  preAwarded: getFieldValue(item?.preAwarded),
  btfRate: getFieldValue(item?.btfRate),
  destHub: getFieldValue(item?.destHub),
  orgHub: getFieldValue(item?.orgHub),
  changeComment: getFieldValue(item?.changeComment),
  ...mapRateToChargeType(item),
});

const parseTableData = (data, tableName) => {
  if (!data?.details?.data?.length) {
    return [];
  }

  return data.details.data
    .filter((item) => ALLOWED_CHARGE_TYPES.has(item?.lineHaulChargeType))
    .map((item) => {
    const commonFields = mapCommonFields(item);
    
    if (tableName === "drmBidTableData") {
      return {
        ...commonFields,
        ...mapBidSpecificFields(item),
      };
    } else if (tableName === "drmDailyTableData") {
      return {
        ...commonFields,
        ...mapDailySpecificFields(item),
      };
    }
    
    return commonFields;
  });
};

export function* workerGetDrmTableDataFetch({ payload }) {
  try {
    const response = yield call(fetchDrmExcelData, [
      payload.reqBody,
      payload.totalPages,
      payload.tableName,
    ]);

    const data = response;
    const parsedShipmentValues = parseTableData(data, payload.tableName);

    const rest =
      payload.tableName === "drmBidTableData" ||
      payload.tableName === "drmDailyTableData"
        ? {
            onPage: data?.details?.onPage,
            pageSize: data?.details?.pageSize,
            totalPages: data?.details?.totalPages,
            totalRecords: data?.details?.totalRecords,
          }
        : { ...data };

    yield put(
      setDrmExcelTableDataSuccess({
        ...rest,
        drmMaintenanceDetails: parsedShipmentValues,
      })
    );
  } catch (error) {
    yield put(setDrmExcelTableDataFailure(error.toString()));
  }
}


function* drmTableDataSaga() {
  yield takeLatest(
    "excelData/getDrmExcelTableData",
    workerGetDrmTableDataFetch
  );
}

export default drmTableDataSaga;
